"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { LocaleCode } from "@/lib/i18n/locales";
import { NAV_SECTIONS, REDIRECT_SECTIONS, DEFAULT_SECTION, type MegaColumn } from "./navMenus";
import { SearchInput } from "./SearchInput";
import { LanguageModal } from "./LanguageModal";
import { T } from "@/lib/i18n/T";
import { useActiveSectionTheme } from "@/hooks/useActiveSectionTheme";
import "./navbar.css";

/* Icons (paths lifted verbatim from the old BoardV2). */
const I = {
  burger: "M3 9C3 8.58579 3.33579 8.25 3.75 8.25H20.25C20.6642 8.25 21 8.58579 21 9C21 9.41421 20.6642 9.75 20.25 9.75H3.75C3.33579 9.75 3 9.41421 3 9ZM3 15.75C3 15.3358 3.33579 15 3.75 15H20.25C20.6642 15 21 15.3358 21 15.75C21 16.1642 20.6642 16.5 20.25 16.5H3.75C3.33579 16.5 3 16.1642 3 15.75Z",
  search: "M13.75 8.125C10.6434 8.125 8.125 10.6434 8.125 13.75C8.125 16.8566 10.6434 19.375 13.75 19.375C15.3035 19.375 16.7089 18.7461 17.7275 17.7275C18.7461 16.7089 19.375 15.3035 19.375 13.75C19.375 10.6434 16.8566 8.125 13.75 8.125ZM6.875 13.75C6.875 9.95304 9.95304 6.875 13.75 6.875C17.547 6.875 20.625 9.95304 20.625 13.75C20.625 15.4232 20.0266 16.9575 19.0333 18.1494L22.9419 22.0581C23.186 22.3021 23.186 22.6979 22.9419 22.9419C22.6979 23.186 22.3021 23.186 22.0581 22.9419L18.1494 19.0333C16.9575 20.0266 15.4232 20.625 13.75 20.625C9.95304 20.625 6.875 17.547 6.875 13.75Z",
  account:
    "M15 6.5C19.6944 6.5 23.5 10.3056 23.5 15C23.5 19.6944 19.6944 23.5 15 23.5C10.3056 23.5 6.5 19.6944 6.5 15C6.5 10.3056 10.3056 6.5 15 6.5ZM12.1837 15.6538C12.3004 18.0912 13.0579 19.7224 13.7894 20.751C14.1893 21.3134 14.586 21.7028 14.8766 21.948C14.9198 21.9845 14.9619 22.0166 15 22.0467C15.0381 22.0166 15.0802 21.9845 15.1234 21.948C15.414 21.7028 15.8107 21.3134 16.2106 20.751C16.9421 19.7224 17.6996 18.0912 17.8163 15.6538H12.1837ZM7.83919 15.6538C8.10957 18.6533 10.2201 21.1191 13.0342 21.9173C12.9315 21.7901 12.827 21.6543 12.7235 21.5087C11.8436 20.2713 10.993 18.3804 10.8743 15.6538H7.83919ZM19.1257 15.6538C19.007 18.3804 18.1564 20.2713 17.2765 21.5087C17.1729 21.6545 17.0677 21.79 16.9649 21.9173C19.7795 21.1194 21.8904 18.6536 22.1608 15.6538H19.1257ZM16.9649 8.08183C17.0679 8.20933 17.1727 8.34533 17.2765 8.49134C18.1564 9.72871 19.007 11.6196 19.1257 14.3462H22.1608C21.8904 11.3463 19.7796 8.8797 16.9649 8.08183ZM14.8766 8.05203C14.586 8.29719 14.1893 8.68663 13.7894 9.24905C13.0579 10.2776 12.3004 11.9088 12.1837 14.3462H17.8163C17.6996 11.9088 16.9421 10.2776 16.2106 9.24905C15.8107 8.68663 15.414 8.29719 15.1234 8.05203C15.08 8.01541 15.0383 7.98267 15 7.95242C14.9617 7.98267 14.92 8.01541 14.8766 8.05203ZM13.0342 8.08183C10.22 8.87994 8.10958 11.3466 7.83919 14.3462H10.8743C10.993 11.6196 11.8436 9.72871 12.7235 8.49134C12.8272 8.34549 12.9314 8.20921 13.0342 8.08183Z",
  cart: "M14.9985 4.92578C16.1941 4.92578 17.1286 5.27006 17.8432 5.81445C18.5499 6.35301 19.0078 7.06076 19.3041 7.73828C19.5896 8.391 19.7334 9.03631 19.808 9.52441H23.2524C23.4713 9.52439 23.6882 9.56922 23.8891 9.65625C24.0899 9.74327 24.2707 9.87059 24.4203 10.0303C24.57 10.19 24.6854 10.3789 24.7592 10.585C24.833 10.791 24.8633 11.0101 24.8491 11.2285L24.0473 23.5752C24.0209 23.9807 23.8409 24.3608 23.5444 24.6387C23.2848 24.8818 22.9532 25.0311 22.602 25.0645L22.4506 25.0713H7.54632C7.1401 25.0712 6.74903 24.9164 6.45257 24.6387C6.15614 24.3609 5.97605 23.9806 5.94964 23.5752L5.14788 11.2285C5.13365 11.0101 5.16489 10.791 5.2387 10.585C5.31248 10.379 5.42702 10.1899 5.57659 10.0303C5.72627 9.87055 5.90797 9.74328 6.10882 9.65625C6.30952 9.56934 6.52585 9.52441 6.74456 9.52441H10.1899C10.2645 9.03626 10.4083 8.39103 10.6938 7.73828C10.9901 7.06076 11.447 6.35301 12.1537 5.81445C12.8683 5.26998 13.8028 4.92582 14.9985 4.92578ZM18.6909 14.7236V10.7246H11.3071V14.7236C11.3069 15.0549 11.0378 15.3232 10.7065 15.3232C10.3753 15.3231 10.107 15.0548 10.1069 14.7236V10.7246H6.74456C6.68985 10.7246 6.63558 10.7361 6.58538 10.7578C6.53533 10.7795 6.4899 10.8108 6.45257 10.8506C6.41518 10.8905 6.38606 10.9378 6.36761 10.9893C6.34916 11.0408 6.34159 11.0958 6.34515 11.1504L7.14788 23.4971C7.15449 23.5984 7.19874 23.6942 7.27288 23.7637C7.34694 23.833 7.44486 23.871 7.54632 23.8711H22.4506C22.5521 23.8711 22.65 23.833 22.7241 23.7637C22.7982 23.6942 22.8434 23.5984 22.85 23.4971L23.6518 11.1504C23.6553 11.0958 23.6478 11.0408 23.6293 10.9893C23.6109 10.9378 23.5817 10.8905 23.5444 10.8506C23.507 10.8108 23.4616 10.7795 23.4116 10.7578C23.3614 10.7361 23.3071 10.7246 23.2524 10.7246H19.891V14.7236C19.8909 15.0549 19.6217 15.3232 19.2905 15.3232C18.9593 15.3231 18.691 15.0548 18.6909 14.7236ZM14.9985 6.125C14.0485 6.12504 13.3737 6.39426 12.8813 6.76953C12.3809 7.15085 12.0325 7.66978 11.7924 8.21875C11.5929 8.67483 11.4767 9.1371 11.4067 9.52441H18.5912C18.5212 9.13706 18.4041 8.67489 18.2045 8.21875C17.9644 7.66993 17.6168 7.15081 17.1166 6.76953C16.6241 6.39423 15.9486 6.125 14.9985 6.125Z",
  location:
    "M12.8171 22.0294C12.6966 22.1738 12.4754 22.1743 12.3546 22.0304C11.4225 20.9213 7.9506 16.6849 6.54499 14.1621C5.97913 13.1467 5.45291 12.1018 5.27131 10.9395C4.99189 9.15415 5.45943 7.52129 6.45269 6.04595C8.12971 3.55528 11.1375 2.33528 14.018 2.93324C17.0294 3.55829 19.2672 5.82272 19.8512 8.82956C20.105 10.1354 19.9073 11.3784 19.3811 12.5889C18.6016 14.3813 17.5782 16.0322 16.4023 17.5818C15.4396 18.85 13.4883 21.2242 12.8171 22.0294ZM16.2658 9.56601C16.2769 7.52028 14.645 5.86335 12.6083 5.85231C10.5641 5.84127 8.89673 7.47514 8.88318 9.50327C8.86963 11.5546 10.49 13.2084 12.5291 13.2245C14.6059 13.241 16.2548 11.6267 16.2658 9.56601Z",
  avatarTop:
    "M10.9968 11.517C14.1772 11.517 16.7553 8.93887 16.7553 5.75852C16.7553 2.57817 14.1772 0 10.9968 0C7.81645 0 5.23828 2.57817 5.23828 5.75852C5.23828 8.93887 7.81645 11.517 10.9968 11.517Z",
  avatarBody:
    "M21.947 23.0341C21.203 20.7144 19.7418 18.6909 17.774 17.2554C15.8059 15.8198 13.4329 15.0463 10.9969 15.0463C8.56097 15.0463 6.18794 15.8198 4.21996 17.2554C2.252 18.6909 0.790738 20.7144 0.046875 23.0341L0.0515282 24H21.9517L21.947 23.0341Z",
};

function Svg({ d, size = 30, vb = 30 }: { d: string; size?: number; vb?: number }) {
  return (
    <svg width={size} height={size} viewBox={`0 0 ${vb} ${vb}`} fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d={d} />
    </svg>
  );
}

export function Navbar({ locale }: { locale: LocaleCode }) {
  const router = useRouter();
  const localeHref = (path: string) => `/${locale}${path || ""}`;
  const [drawerQuery, setDrawerQuery] = useState("");
  function drawerSearch(e: FormEvent) {
    e.preventDefault();
    const q = drawerQuery.trim();
    setDrawer(false);
    router.push(localeHref(`/search${q ? `?q=${encodeURIComponent(q)}` : ""}`));
  }

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false); // desktop mega-menu hover state
  const [drawer, setDrawer] = useState(false); // mobile
  const [section, setSection] = useState<string>(DEFAULT_SECTION);
  const [selection, setSelection] = useState<string>("");
  const [cll, setCll] = useState(false); // language / quick-settings modal

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY >= 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!drawer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawer]);

  // Smooth-scroll the drawer back to the top when drilling in/out of a submenu.
  const drawerScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    drawerScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [selection]);

  // Adapt the bar foreground to the theme of the section behind it.
  const sectionTheme = useActiveSectionTheme();
  const stateClass = [
    // the open mobile drawer wants a solid black bar (matches the dark drawer)
    drawer ? "is-drawer" : open ? "is-open" : scrolled ? "is-scrolled" : "",
    sectionTheme === "light" && !open && !drawer ? "is-light" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const current = NAV_SECTIONS[section];
  const activeSub = selection ? current?.submenus[selection] : undefined;
  const showScreen = open && !!activeSub?.blocks;

  return (
    <>
    <header
      className={`rubyn-nav ${stateClass}`}
      onMouseLeave={() => {
        setOpen(false);
        setSelection("");
      }}
    >
      <div className="rubyn-nav-group" onMouseEnter={() => setOpen(true)}>
        {/* ---- Top bar ---- */}
        <div className="rby__nav">
          <div className="rby__nav-container">
            <div className="rby__nav-container-l csx3-f1 csx3-R01">
              <button
                className="rubyn-nav-burger"
                aria-label={drawer ? "Close menu" : "Open menu"}
                onClick={() => setDrawer((v) => !v)}
              >
                {drawer ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                ) : (
                  <Svg d={I.burger} size={26} vb={24} />
                )}
              </button>

              <Link href={localeHref("")} className="rubyn-nav-logo" aria-label="RUBYN home">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="rubyn-nav-logo-img"
                  src="/media/rubyn-metal-mobile.webp"
                  alt="RUBYN"
                  width={188}
                  draggable={false}
                />
              </Link>

              <nav className="rby__nav-container-l-hmenus" aria-label="Sections">
                {Object.entries(NAV_SECTIONS).map(([key, s]) => {
                  const el = (
                    <h4><T id={s.titleId} /></h4>
                  );
                  const cls = `rby__nav-container-l-hmenus__el csx3-R11 ${section === key ? "active" : ""}`;
                  return REDIRECT_SECTIONS.includes(key) && s.canonical ? (
                    <Link
                      key={key}
                      href={localeHref(s.canonical)}
                      className={cls}
                      onMouseEnter={() => {
                        setSection(key);
                        setSelection("");
                      }}
                    >
                      {el}
                    </Link>
                  ) : (
                    <div key={key} className={cls} onMouseEnter={() => { setSection(key); setSelection(""); }}>
                      {el}
                    </div>
                  );
                })}
              </nav>
            </div>

            <div className="rby__nav-container-r csx3-R21">
              <SearchInput locale={locale} />

              <Link href={localeHref("/stores")} className="rubyn-nav-locations">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="currentColor" aria-hidden="true">
                  <path d={I.location} />
                </svg>
                <span className="rubyn-nav-locations-text csx3-C00">
                  <h4><T id="nav.beautyStore.title" /></h4>
                  <h5><T id="nav.beautyStore.text" /></h5>
                </span>
              </Link>

              <button
                className="rubyn-nav-icon"
                aria-label="Language & settings"
                onClick={() => setCll(true)}
              >
                <Svg d={I.account} size={30} vb={30} />
              </button>
              <Link href={localeHref("/cart")} className="rubyn-nav-icon" aria-label="Cart">
                <Svg d={I.cart} size={30} vb={30} />
              </Link>
              <Link href={localeHref("/dashboard")} className="rubyn-nav-avatar" aria-label="Profile">
                <svg width="20" height="22" viewBox="0 0 22 24" fill="currentColor" aria-hidden="true">
                  <path d={I.avatarBody} />
                  <path d={I.avatarTop} />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* ---- Mobile section tabs (helper row) ---- */}
        <div className="rby__helper">
          {Object.entries(NAV_SECTIONS).map(([key, s]) => (
            <button
              key={key}
              className={`rby__nav-container-l-hmenus__el csx3-R11 ${section === key ? "active" : ""}`}
              onClick={() => {
                setSection(key);
                setSelection("");
                setDrawer(true);
              }}
            >
              <h4><T id={s.titleId} /></h4>
            </button>
          ))}
        </div>

        {/* ---- Secondary submenu row ---- */}
        <div className="rby__menus">
          <div className="rby__menus-container csx3-f1 csx3-R01">
            {Object.entries(current?.submenus ?? {}).map(([key, sub]) => (
              <div
                key={key}
                className={`rby__menus__el csx3-f1 csx3-R11 ${selection === key ? "active" : ""}`}
                onMouseEnter={() => setSelection(key)}
              >
                {sub.canonical ? (
                  <Link href={localeHref(sub.canonical)}>
                    <h5><T id={sub.titleId} /></h5>
                  </Link>
                ) : (
                  <h5><T id={sub.titleId} /></h5>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---- Mega-menu screen ---- */}
        {showScreen && activeSub?.blocks && (
          <div className="rby__screen">
            <div className="rby__screen-container csx3-f1 csx3-R00 csx3-fw">
              {activeSub.blocks.map((b, i) =>
                b.type === "column" ? (
                  <div key={i} className="rby__screen-container__menu">
                    {b.titleId && <h4><T id={b.titleId} /></h4>}
                    <div className="rby__screen-container__menu-column csx3-C">
                      {b.links.map((l, z) => (
                        <Link
                          key={z}
                          href={localeHref(l.canonical)}
                          className={`rby__screen-container__menu-column__el ${l.bold ? "bold" : ""}`}
                        >
                          <T id={l.textId} />
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link key={i} href={localeHref(b.canonical)} className="rby__screen-container__image">
                    <span
                      className="rby__screen-container__image-img"
                      style={{ backgroundImage: `url(${b.src})` }}
                    />
                    <h5><T id={b.textId} /></h5>
                  </Link>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </header>

      {/* ---- Mobile drawer: drill-down (faithful to the old naturalmobile) ---- */}
      {drawer &&
        (() => {
          const sub = selection ? current?.submenus[selection] : undefined;
          const cols = (sub?.blocks?.filter((b) => b.type === "column") ?? []) as MegaColumn[];
          return (
            <div ref={drawerScrollRef} className="rby__naturalmobile csx3-C00 csx3-g-10">
              <form className="rby__mobile-search csx3-R01 csx3-g-12 csx3-w-full" onSubmit={drawerSearch} role="search">
                <Svg d={I.search} size={18} vb={30} />
                <input
                  type="search"
                  className="csx3-f1"
                  placeholder="Search products, brands…"
                  value={drawerQuery}
                  onChange={(e) => setDrawerQuery(e.target.value)}
                  aria-label="Search"
                />
              </form>
              {selection && cols.length > 0 ? (
                <>
                  <button
                    className="rby__naturalmobile-bg csx3-R01 csx3-g-12 csx3-w-full csx3-active-press"
                    onClick={() => setSelection("")}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                    <h4><T id={sub!.titleId} /></h4>
                  </button>
                  {cols.map((b, i) => (
                    <div key={i} className="rby__naturalmobile-z csx3-C00 csx3-w-full">
                      {b.titleId && (
                        <div className="rby__naturalmobile-z-title csx3-w-full">
                          <h5><T id={b.titleId} /></h5>
                        </div>
                      )}
                      <div className="csx3-C00 csx3-w-full">
                        {b.links.map((l, z) => (
                          <Link
                            key={z}
                            href={localeHref(l.canonical)}
                            className={`rby__nav_mobile_row csx3-R01 csx3-g-12 csx3-w-full csx3-active-press ${l.bold ? "bold" : ""}`}
                            onClick={() => setDrawer(false)}
                          >
                            <p className="csx3-f1"><T id={l.textId} /></p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="csx3-C00 csx3-w-full">
                  {Object.entries(current?.submenus ?? {}).map(([k, s2]) =>
                    s2.blocks ? (
                      <button
                        key={k}
                        className="rby__nav_mobile_row csx3-R01 csx3-g-12 csx3-w-full csx3-active-press"
                        onClick={() => setSelection(k)}
                      >
                        <p className="csx3-f1"><T id={s2.titleId} /></p>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        key={k}
                        href={localeHref(s2.canonical ?? current?.canonical ?? "/")}
                        className="rby__nav_mobile_row csx3-R01 csx3-g-12 csx3-w-full csx3-active-press"
                        onClick={() => setDrawer(false)}
                      >
                        <p className="csx3-f1"><T id={s2.titleId} /></p>
                      </Link>
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })()}

      {cll && <LanguageModal locale={locale} onClose={() => setCll(false)} />}
    </>
  );
}
